/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

const normalizeValues = (value) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === '') return [];
  return [String(value)];
};

const ModernMultiCheckboxSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  const selected = useMemo(() => normalizeValues(value), [value]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const optionMap = useMemo(() => {
    const m = new Map();
    (options || []).forEach((o) => {
      const k = String(o.value ?? '').trim();
      if (k) m.set(k, o);
    });
    return m;
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = String(searchTerm || '').toLowerCase().trim();
    return (options || []).filter((o) => {
      if (!q) return true;
      const v = String(o.value ?? '').toLowerCase();
      const l = String(o.label ?? '').toLowerCase();
      return v.includes(q) || l.includes(q);
    });
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Match typical "Select" UX: fresh search each open (important for modals that don't unmount).
  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const emitChange = (vals) => {
    if (typeof onChange === 'function') onChange(vals);
  };

  const toggleValue = (raw) => {
    const v = String(raw ?? '').trim();
    if (!v) return;
    if (selectedSet.has(v)) {
      emitChange(selected.filter((x) => x !== v));
    } else {
      emitChange([...selected, v]);
    }
  };

  const removeValue = (v) => emitChange(selected.filter((x) => x !== v));

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen((v) => !v)}
        style={{
          minHeight: '40px',
          padding: '4px 10px',
          borderRadius: '8px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #D1D5DB',
          backgroundColor: disabled ? '#F9FAFB' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxShadow: isOpen ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, overflow: 'hidden' }}>
          {selected.length > 0 ? (
            selected.map((v) => {
              const label = optionMap.get(v)?.label || v;
              return (
                <span
                  key={v}
                  style={{
                    backgroundColor: '#EFF6FF',
                    color: '#1E40AF',
                    fontSize: '12px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    border: '1px solid #DBEAFE',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {label}
                  {!disabled && (
                    <X
                      size={12}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeValue(v);
                      }}
                    />
                  )}
                </span>
              );
            })
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: '14px' }}>{placeholder}</span>
          )}
        </div>
        {isOpen ? <ChevronUp size={18} color="#3B82F6" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>

      {isOpen && !disabled && (
        <div
          onWheel={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            zIndex: 1100,
            backgroundColor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <input
            autoFocus
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              height: '36px',
              padding: '8px 10px',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              fontSize: '14px',
              outline: 'none',
              color: '#1F2937',
              boxSizing: 'border-box',
              backgroundColor: '#fff',
            }}
          />

          <div
            onWheel={(e) => e.stopPropagation()}
            style={{
              maxHeight: '220px',
              overflowY: 'auto',
              paddingRight: '4px',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const optVal = String(opt.value ?? '').trim();
                const checked = optVal !== '' && selectedSet.has(optVal);
                return (
                  <div
                    key={optVal || String(opt.label)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleValue(optVal);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '7px 10px',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      marginBottom: '4px',
                      backgroundColor: checked ? '#F3F4F6' : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        border: checked ? 'none' : '1px solid #D1D5DB',
                        borderRadius: '4px',
                        backgroundColor: checked ? '#3B82F6' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px',
                      }}
                    >
                      {checked && <CheckOutlined style={{ color: 'white', fontSize: '10px' }} />}
                    </div>
                    <span style={{ fontSize: '14px', color: '#374151', fontWeight: checked ? 500 : 400 }}>
                      {opt.label}
                    </span>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernMultiCheckboxSelect;
